<script lang="ts" setup>
import { productApi } from '~/api'
import type { Product } from '@xianlaibang/shared/types'
import serviceRechargeIcon from '~/assets/scss/static/组 1609@2x.png'
import serviceTaskIcon from '~/assets/scss/static/组 1610@2x.png'
// import serviceCardIcon from '~/assets/scss/static/组 1608@2x.png'
import rechargePanelBg from '~/assets/scss/static/蒙版组 52@2x.png'

/** 首页话费充值产品分类 ID */
const RECHARGE_CATEGORY_ID = 1

type ServiceKey = 'recharge' | 'task'

const HOME_SERVICE_STORAGE_KEY = 'home_active_service'

// 用户真正“选中”的服务（点击产生，需返回后保持）
const selectedService = ref<ServiceKey>('recharge')
// 鼠标悬停时的临时高亮（不持久化）
const hoveredService = ref<ServiceKey | null>(null)

const activeService = computed<ServiceKey>(() => hoveredService.value ?? selectedService.value)

const serviceCards: { 
  key: ServiceKey
  title: string
  desc: string
  icon: string
  path: string
}[] = [
  {
    key: 'recharge',
    title: '话费采购',
    desc: '拥有海量品牌授权，平台货源丰富，',
    icon: serviceRechargeIcon,
    path: '/user/direct',
  },
  {
    key: 'task',
    title: '任务发布',
    desc: '拥有海量品牌授权，平台货源丰富，',
    icon: serviceTaskIcon,
    path: '/user/task/release',
  },
  // {
  //   key: 'card',
  //   title: '卡券交易',
  //   desc: '拥有海量品牌授权，平台货源丰富，',
  //   icon: serviceCardIcon,
  //   path: '/user/direct',
  // },
]

function handleServiceClick(item: (typeof serviceCards)[number]) {
  selectedService.value = item.key
  try {
    sessionStorage.setItem(HOME_SERVICE_STORAGE_KEY, item.key)
  } catch {
    // ignore (private mode / disabled storage)
  }
  if (item.key === 'task') {
    navigateTo(item.path)
  }
  if (item.key === 'recharge') {
    navigateTo(item.path)
  }
}

const loading = ref(false)
const rechargeProducts = ref<Product[]>([])

async function fetchRechargeProducts() {
  loading.value = true
  try {
    const { data } = await productApi.getProductList(RECHARGE_CATEGORY_ID)
    rechargeProducts.value = data || []
  } catch (error) {
    console.error(error)
    rechargeProducts.value = []
  } finally {
    loading.value = false
  }
}

function getProductIcon(icon: string) {
  if (!icon) return ''
  if (icon.startsWith('http://') || icon.startsWith('https://')) return icon
  return useOssImage(icon.startsWith('/') ? icon : `/${icon}`)
}

function getRechargeLink(item: Product) {
  return {
    path: '/user/direct',
    query: {
      category: RECHARGE_CATEGORY_ID,
      product: item.rechargeGoodsId,
    },
  }
}

onMounted(() => {
  try {
    const saved = sessionStorage.getItem(HOME_SERVICE_STORAGE_KEY) as ServiceKey | null
    if (saved === 'recharge' || saved === 'task') {
      selectedService.value = saved
    } else {
      selectedService.value = 'recharge'
    }
  } catch {
    selectedService.value = 'recharge'
  }
  fetchRechargeProducts()
})
</script>

<template>
  <div class="merchant-products">
    <div class="service-intro">
      <div
        v-for="item in serviceCards"
        :key="item.title"
        class="service-card"
        :class="{ active: activeService === item.key }"
        @mouseenter="hoveredService = item.key"
        @mouseleave="hoveredService = null"
        @click="handleServiceClick(item)"
      >
        <div class="service-card__body">
          <div class="service-card__icon-wrap">
            <img :src="item.icon" alt="" class="service-card__icon" />
          </div>
          <div class="service-card__content">
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </div>
        </div>
        <div class="service-card__line" aria-hidden="true" />
      </div>
    </div>

    <section class="recharge-panel">
      <img class="recharge-panel__bg" :src="rechargePanelBg" alt="" />
      <div class="recharge-panel__content">
        <div class="recharge-panel__header">
          <h3>话费充值</h3>
          <p>支持三大主流平台的话费虚拟商品充值服务，用户下单后，自动发货，确保订单实时同步，实现无忧对账，简单赚钱～</p>
        </div>

        <div v-loading="loading" class="recharge-panel__list">
          <NuxtLink
            v-for="item in rechargeProducts"
            :key="item.rechargeGoodsId"
            :to="getRechargeLink(item)"
            class="recharge-card"
          >
            <img :src="getProductIcon(item.icon)" :alt="item.name" class="recharge-card__icon" />
            <h4 class="recharge-card__name">{{ item.name }}</h4>
            <span class="recharge-card__btn">我要采购</span>
          </NuxtLink>
        </div>
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.merchant-products {
  display: flex;
  flex-direction: column;
  gap: 34px;
}

.service-intro {
  display: flex;
  align-items: stretch;
  width: 100%;
}

.service-card {
  position: relative;
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  min-width: 0;
  padding: 0 40px;
  border: none;
  border-radius: 0;
  background: transparent;
  color: inherit;
  text-decoration: none;
  box-shadow: none;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }

  &:hover {
    opacity: 0.92;
  }

  &.active {
    .service-card__content h3 {
      color: #ff7445;
    }

    .service-card__line {
      height: 4px;
      border-radius: 2px;
      background: linear-gradient(90deg, #ff7445 0%, #ff9a5c 100%);
    }
  }

  &__body {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    padding-bottom: 20px;
  }

  &__icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    margin-right: 18px;
    flex-shrink: 0;
  }

  &__icon {
    display: block;
    width: 72px;
    height: 72px;
    object-fit: contain;
  }

  &__content {
    min-width: 0;

    h3 {
      margin: 0;
      color: #12233b;
      font-size: 22px;
      font-weight: 700;
      line-height: 1.2;
    }

    p {
      margin: 10px 0 0;
      color: #8ea2b6;
      font-size: 14px;
      line-height: 1.5;
    }
  }

  &__line {
    width: 100%;
    height: 1px;
    border-radius: 1px;
    background: #e3eaf2;
  }
}

.recharge-panel {
  position: relative;
  min-height: 228px;
  padding: 34px 40px 30px;
  overflow: hidden;
  border-radius: 8px;
  // background: linear-gradient(90deg, #f9fcff 0%, #f2f8ff 48%, #eaf4ff 100%);

  &__bg {
    position: absolute;
    top: 0;
    right: 0;
    width: min(62%, 620px);
    height: 100%;
    object-fit: contain;
    object-position: right center;
    pointer-events: none;
  }

  &__content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 26px;
    max-width: 100%;
  }

  &__header {
    max-width: 620px;

    h3 {
      margin: 0;
      color: #12233b;
      font-size: 24px;
      font-weight: 700;
      line-height: 1.2;
    }

    p {
      margin: 14px 0 0;
      color: #8ea2b6;
      font-size: 14px;
      line-height: 1.85;
    }
  }

  &__list {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px;
    max-width: 100%;
  }
}

.recharge-card {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  min-height: 88px;
  padding: 18px 22px;
  border: none;
  border-radius: 10px;
  background: #F9FAFB;
  color: inherit;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(47, 107, 171, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(47, 107, 171, 0.12);
  }

  &__icon {
    width: 52px;
    height: 52px;
    object-fit: contain;
    flex-shrink: 0;
  }

  &__name {
    flex: 1;
    min-width: 0;
    margin: 0;
    color: #12233b;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
  }

  &__btn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 96px;
    height: 36px;
    padding: 0 18px;
    border: 1px solid #ff7445;
    border-radius: 999px;
    background: #fff;
    color: #ff7445;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    transition: background 0.2s ease, color 0.2s ease;
  }

  &:hover &__btn {
    background: #ff7445;
    color: #fff;
  }
}

@media (max-width: 1000px) {
  .service-intro {
    flex-direction: column;
    gap: 28px;
  }

  .service-card {
    padding: 0;
  }

  .recharge-panel {
    padding: 28px 24px 24px;

    &__bg {
      width: 72%;
      opacity: 0.35;
    }

    &__header p {
      max-width: 100%;
    }

    &__list {
      grid-template-columns: 1fr;
    }
  }

  .recharge-card {
    min-height: 80px;
    padding: 16px 18px;

    &__name {
      font-size: 16px;
    }

    &__btn {
      min-width: 88px;
      height: 34px;
      font-size: 13px;
    }
  }
}
</style>
